import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';

import { SignalRService } from './signalr.service';
import { TripStateService, TripPhase } from './trip-state.service';
import { CrudService } from './crud.service';
import { Events } from './events-service.service';
import { HUB_EVENTS } from 'src/models/signalr-events.model';
import {
  RaceRequestEvent,
  DriverPositionEvent,
} from 'src/models/signalr-events.model';
import {
  AcceptRaceViewModel,
  EnterInLineViewModel,
} from 'src/models/user.model';
import EnterQueueRuleResult from 'src/models/enterQueueRuleResult.model';

/**
 * DriverQueueFacadeService — Phase 2.4
 *
 * Orchestrates the driver queue lifecycle:
 *  • Wires hub events into TripStateService
 *  • Exposes typed Observables for events the page must react to
 *  • Provides action methods so info-queue.page.ts stays as a thin controller
 */
@Injectable({ providedIn: 'root' })
export class DriverQueueFacadeService implements OnDestroy {
  // ── State selectors (delegated to TripStateService) ──────────────────────
  readonly phase$: Observable<TripPhase> = this._tripState.phase$;
  readonly queuePosition$: Observable<number> = this._tripState.queuePosition$;
  readonly queueSize$: Observable<number> = this._tripState.queueSize$;
  readonly activeRequest$: Observable<RaceRequestEvent> = this._tripState.activeRequest$;

  // ── Events the page must handle (navigation / UI) ─────────────────────────
  /** Emits when the server notifies this driver has been kicked. */
  readonly driverKicked$: Observable<boolean> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.DRIVER_KICKED),
    map(e => e.payload as boolean),
    share(),
  );

  /** Emits when the caller cancels the trip while driver is in-trip. */
  readonly callerCancelledRace$: Observable<any> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.CANCEL_TRIP),
    map(e => e.payload),
    share(),
  );

  /** Emits when the race/tour has been confirmed accepted (OnAccept). */
  readonly tripAccepted$: Observable<RaceRequestEvent> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.ON_ACCEPT),
    map(e => e.payload as RaceRequestEvent),
    share(),
  );

  /** Emits on verify-connection server ping (used to refresh queue notification). */
  readonly connectionVerified$: Observable<boolean> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.VERIFY_CONNECTION),
    map(e => e.payload as boolean),
    share(),
  );

  /** Emits the current list of disputed trips from the hub. */
  readonly disputedTripsFromHub$: Observable<any[]> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.DRIVER_TRIPS_DISPUTE),
    map(e => e.payload as any[]),
    share(),
  );

  private _subs = new Subscription();

  constructor(
    private _signalR: SignalRService,
    private _tripState: TripStateService,
    private _crud: CrudService,
    private _events: Events,
  ) {
    this._wireEventHandlers();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  // ── Hub event → TripState wiring ─────────────────────────────────────────

  private _wireEventHandlers(): void {
    // Driver position updates
    this._subs.add(
      this._signalR.events$.pipe(
        filter(e => e.type === HUB_EVENTS.DRIVER_POSITION),
        map(e => e.payload as DriverPositionEvent),
      ).subscribe(pos => this._tripState.updateQueuePosition(pos)),
    );

    // Incoming race request
    this._subs.add(
      this._signalR.events$.pipe(
        filter(e => e.type === HUB_EVENTS.RACE_REQUEST),
        map(e => e.payload as RaceRequestEvent),
      ).subscribe(req => {
        localStorage.setItem('raceRequest', JSON.stringify(req));
        this._tripState.receiveRequest(req);
      }),
    );

    // Server-side cancel (countdown expired / seller cancelled before acceptance)
    this._subs.add(
      this._signalR.events$.pipe(
        filter(e => e.type === HUB_EVENTS.CANCEL_RACE_REQUEST),
      ).subscribe(() => this._tripState.declineRequest()),
    );

    // Race accepted → move to in-trip
    this._subs.add(
      this._signalR.events$.pipe(
        filter(e => e.type === HUB_EVENTS.ON_ACCEPT),
        map(e => e.payload as RaceRequestEvent),
      ).subscribe(trip => this._tripState.beginTrip(trip)),
    );
  }

  // ── Action methods ────────────────────────────────────────────────────────

  /**
   * Sets up the SignalR hub connection for the given driver viewmodel.
   * Does NOT enter the queue — call `enterQueue` next.
   */
  async setupConnection(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.setUpConnection(viewModel);
  }

  /**
   * Validates queue entry rules.  Returns `canEnterQueue` result.
   */
  async validateQueueEntry(driverId: string): Promise<EnterQueueRuleResult> {
    return this._signalR.getEnterQueueRuleValidation(driverId);
  }

  /**
   * Enters the driver queue via SignalR and updates TripState to `in-queue`.
   */
  async enterQueue(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.setDriverEnterInLine(viewModel);
    this._tripState.enterQueue();
    this._events.publish('enterLine', true);
  }

  /**
   * Re-enters the queue after accepting a race (acceptedRace = true).
   */
  async reenterQueue(viewModel: EnterInLineViewModel): Promise<void> {
    viewModel.acceptedRace = true;
    await this._signalR.setDriverEnterInLine(viewModel);
    this._tripState.declineRequest();
  }

  /**
   * Leaves the driver queue and resets TripState to `idle`.
   */
  async leaveQueue(viewModel: EnterInLineViewModel): Promise<void> {
    if (this._signalR.hubConnection) {
      await this._signalR.setLeaveLine(viewModel);
    }
    this._tripState.leaveQueue();
    this._events.publish('enterLine', false);
  }

  /**
   * Accepts a race or tour depending on `activeRequest.latitudeDestino`.
   * Returns true if the server accepted the request.
   */
  async acceptRace(acceptRaceViewModel: AcceptRaceViewModel): Promise<boolean> {
    const activeRequest = this._tripState.currentState.activeRequest;
    if (activeRequest?.latitudeDestino) {
      const ok = await this._signalR.setAcceptRace(acceptRaceViewModel);
      if (ok) this._tripState.acceptRequest();
      return !!ok;
    } else {
      const ok = await this._signalR.setAcceptTour(acceptRaceViewModel);
      if (ok) {
        await this._signalR.setLeaveLine({} as EnterInLineViewModel);
        this._tripState.acceptRequest();
      }
      return !!ok;
    }
  }

  /**
   * Declines the current race request and updates TripState back to `in-queue`.
   */
  async declineRace(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.setRefuseRace(viewModel);
    this._tripState.declineRequest();
  }

  /**
   * Reconnects the SignalR hub after resume (calls connect2 which stops/starts).
   */
  async reconnect(): Promise<void> {
    await this._signalR.connect2();
  }

  // ── Data methods ──────────────────────────────────────────────────────────

  /** HTTP: returns the list of trips currently in dispute for this driver. */
  getDisputedTrips(): Observable<any[]> {
    return this._crud.getData('Corrida/driver/em-disputa') as Observable<any[]>;
  }

  /** HTTP: returns an uncalled trip if one is pending for this driver. */
  getUncalledTrip(): Observable<any> {
    return this._crud.getData('Corrida/corrida-chamada');
  }

  /** HTTP: notifies backend of cold start (fire-and-forget). */
  coldStart(): Observable<any> {
    return this._crud.postData('motorista/cold-start', null, null);
  }

  /** HTTP: updates driver location on the backend. */
  updateLocation(motoristaId: string, latitude: string, longitude: string, heading: number): Observable<any> {
    return this._crud.patchData('motorista/update-location/', {
      motoristaId,
      latitude,
      longitude,
      heading,
    });
  }

  /** HTTP: fetches disputed trips count on a polling interval. */
  getDisputedTripsCount(): Observable<number> {
    return new Observable(observer => {
      this.getDisputedTrips().subscribe({
        next: (trips: any[]) => observer.next(trips?.length ?? 0),
        error: () => observer.next(0),
        complete: () => observer.complete(),
      });
    });
  }
}
